import { Box, Flex, HStack, Icon, Text } from '@chakra-ui/react';
import type { ElementType } from 'react';
import { Icon as YaxleIcon, type IconName } from '../../../ui';
import { PARTBAY_BLUE, PARTBAY_BLUE_TINT, SETTINGS_BORDER_COLOR } from './constants';

export interface SettingsChoice<T extends string> {
    value: T;
    label: string;
    /** One line under the label, inside the card. */
    description?: string;
    /**
     * Shown in a tinted tile above the label, as on a flow's opening step. Omit
     * for a text-only card.
     */
    icon?: ElementType;
    /** Prefer this over `icon` — a Yaxle glyph, not a react-icons component. */
    iconName?: IconName;
    isDisabled?: boolean;
}

export interface SettingsChoiceCardsProps<T extends string> {
    choices: ReadonlyArray<SettingsChoice<T>>;
    value: T;
    onChange: (value: T) => void;
}

/**
 * A small set of mutually exclusive options as side-by-side cards, each
 * carrying its own explanation. Use where a radio group would need a
 * description per option — the card is the click target, so the explanation
 * sits inside the choice rather than trailing it.
 *
 * For options that need no explanation, SettingsSegmentedControl stays the
 * lighter treatment.
 */
const SettingsChoiceCards = <T extends string>({
    choices,
    value,
    onChange,
}: SettingsChoiceCardsProps<T>) => (
    <HStack spacing={3} align="stretch">
        {choices.map((choice) => {
            const isSelected = choice.value === value;
            return (
                <Box
                    key={choice.value}
                    as="button"
                    type="button"
                    flex={1}
                    minW={0}
                    textAlign="left"
                    px={4}
                    py={3}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={isSelected ? PARTBAY_BLUE : SETTINGS_BORDER_COLOR}
                    bg={isSelected ? PARTBAY_BLUE_TINT : 'white'}
                    opacity={choice.isDisabled ? 0.5 : 1}
                    cursor={choice.isDisabled ? 'not-allowed' : 'pointer'}
                    transition="border-color 0.15s ease, background 0.15s ease"
                    _hover={
                        choice.isDisabled || isSelected
                            ? undefined
                            : { borderColor: PARTBAY_BLUE, bg: 'gray.50' }
                    }
                    onClick={() => !choice.isDisabled && onChange(choice.value)}
                    aria-pressed={isSelected}
                >
                    {(choice.iconName || choice.icon) && (
                        <Flex
                            boxSize="32px"
                            borderRadius="md"
                            align="center"
                            justify="center"
                            mb={2.5}
                            // The tile tracks the card so a selected card reads
                            // as one blue block rather than a blue card with a
                            // grey chip in it.
                            bg={isSelected ? 'white' : 'gray.100'}
                            color={isSelected ? PARTBAY_BLUE : 'gray.600'}
                        >
                            {choice.iconName ? (
                                <YaxleIcon name={choice.iconName} size="sm" />
                            ) : (
                                <Icon as={choice.icon} boxSize={4} />
                            )}
                        </Flex>
                    )}
                    <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={isSelected ? PARTBAY_BLUE : 'gray.800'}
                    >
                        {choice.label}
                    </Text>
                    {choice.description && (
                        <Text fontSize="xs" color="gray.500" mt={1}>
                            {choice.description}
                        </Text>
                    )}
                </Box>
            );
        })}
    </HStack>
);

export default SettingsChoiceCards;
